import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, ChevronRight, CreditCard, UserCog, Headphones, LogOut,
  Moon, Sun, Car, Mail, Phone, ShieldCheck, Tag, BadgeCheck,
} from "lucide-react";
import Avatar from "@/components/Avatar";
import Modal from "@/components/Modal";
import VehicleImage from "@/components/VehicleImage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/format";
import { toast } from "sonner";

function memberDuration(createdAtSec) {
  if (!createdAtSec) return "New";
  const months = Math.floor((Date.now() / 1000 - createdAtSec) / (30 * 24 * 3600));
  if (months < 1) return "New";
  if (months < 12) return `${months} mo`;
  const years = Math.floor(months / 12);
  return years === 1 ? "1 yr" : `${years} yrs`;
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-brand-600"><Icon className="h-4.5 w-4.5" /></span>
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="flex-1 truncate text-right text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

function LinkRow({ icon: Icon, label, onClick, danger, testID }) {
  return (
    <button onClick={onClick} data-testid={testID}
      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-4 shadow-soft transition-colors hover:bg-secondary">
      <span className={cn("flex h-9 w-9 items-center justify-center rounded-full", danger ? "bg-destructive/10 text-destructive" : "bg-accent text-brand-600")}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <span className={cn("flex-1 text-left font-semibold", danger ? "text-destructive" : "text-foreground")}>{label}</span>
      {!danger && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
    </button>
  );
}

export default function AccountView() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { dark, toggle } = useTheme();
  const [profile, setProfile] = useState(null);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const isDriver = user?.role === "driver";
  const vDesc = `${user?.color || ""} ${user?.vehicle || ""}`.trim();

  useEffect(() => { api("/me/profile").then(setProfile).catch(() => {}); }, []);

  const logout = () => { signOut(); toast.success("Signed out"); navigate("/auth", { replace: true }); };
  const startSupport = async () => {
    try { const r = await api("/support/start", { method: "POST" }); navigate(`/chat/${r.ride_id}`); }
    catch { toast.error("Could not start support chat."); }
  };

  const rows = [
    { icon: Mail, label: "Email", value: user?.email },
    { icon: Phone, label: "Phone", value: user?.phone || "—" },
    { icon: ShieldCheck, label: "Account type", value: isDriver ? "Driver" : "Rider" },
  ];

  return (
    <div className="mx-auto max-w-2xl px-5 py-6">
      {/* Head */}
      <div className="mb-5 flex flex-col items-center gap-1">
        <Avatar src={user?.photo} name={user?.name} size={84} />
        <p className="mt-1 text-xl font-extrabold text-foreground">{user?.name}</p>
        <p className="text-sm font-medium text-brand-600">{isDriver ? "Getaride Driver" : "Getaride Rider"}</p>
      </div>

      {/* Stats */}
      <div className="mb-4 flex items-center rounded-2xl border border-border bg-card py-4 shadow-soft" data-testid="profile-stats">
        <div className="flex flex-1 flex-col items-center">
          <p className="font-num text-xl font-extrabold text-foreground">{profile ? profile.total_rides : "—"}</p>
          <p className="text-xs text-muted-foreground">Rides</p>
        </div>
        <span className="h-8 w-px bg-border" />
        <div className="flex flex-1 flex-col items-center">
          <p className="flex items-center gap-1 font-num text-xl font-extrabold text-foreground">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {Number(profile?.rating ?? user?.rating ?? 5).toFixed(1)}
          </p>
          <p className="text-xs text-muted-foreground">Rating</p>
        </div>
        <span className="h-8 w-px bg-border" />
        <div className="flex flex-1 flex-col items-center">
          <p className="font-num text-xl font-extrabold text-foreground">{memberDuration(profile?.created_at ?? user?.created_at)}</p>
          <p className="text-xs text-muted-foreground">On Getaride</p>
        </div>
      </div>

      {/* Info card */}
      <div className="mb-4 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
        {rows.map((r) => <InfoRow key={r.label} icon={r.icon} label={r.label} value={r.value} />)}
      </div>

      {isDriver && (
        <button onClick={() => setVehicleOpen(true)} data-testid="vehicle-card"
          className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-soft transition-colors hover:bg-secondary">
          <VehicleImage desc={vDesc} className="h-14 w-24 shrink-0 rounded-lg" iconSize={24} testID="account-vehicle-image" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-bold text-foreground">{user?.vehicle || "Your vehicle"}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.vehicle_class_label || "Vehicle"} · {user?.plate || "—"}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </button>
      )}

      <Modal open={vehicleOpen} onClose={() => setVehicleOpen(false)} title="Your vehicle" size="md">
        <VehicleImage desc={vDesc} className="mb-4 h-44 w-full rounded-2xl" iconSize={48} testID="vehicle-modal-image" />
        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
          <InfoRow icon={Car} label="Vehicle" value={user?.vehicle || "—"} />
          <InfoRow icon={Tag} label="Color" value={user?.color || "—"} />
          <InfoRow icon={BadgeCheck} label="Category" value={user?.vehicle_class_label || "—"} />
          <InfoRow icon={Tag} label="Plate" value={user?.plate || "—"} />
          {user?.license_number && <InfoRow icon={ShieldCheck} label="License #" value={user.license_number} />}
        </div>
      </Modal>

      {/* Dark mode + links */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-4 shadow-soft">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-brand-600">
            {dark ? <Moon className="h-4.5 w-4.5" /> : <Sun className="h-4.5 w-4.5" />}
          </span>
          <span className="flex-1 font-semibold text-foreground">Dark mode</span>
          <button onClick={toggle} data-testid="account-theme-toggle" aria-label="Toggle dark mode"
            className={cn("relative h-7 w-12 rounded-full transition-colors", dark ? "bg-brand-600" : "bg-border")}>
            <span className={cn("absolute top-1 h-5 w-5 rounded-full bg-white transition-all", dark ? "left-6" : "left-1")} />
          </button>
        </div>
        <LinkRow icon={UserCog} label="Edit profile" onClick={() => navigate("/edit-profile")} testID="edit-profile-row" />
        {!isDriver && <LinkRow icon={CreditCard} label="Payment methods" onClick={() => navigate("/payment-methods")} testID="payment-methods-row" />}
        <LinkRow icon={Headphones} label="Contact support" onClick={startSupport} testID="support-row" />
        {isDriver && <LinkRow icon={ShieldCheck} label="Driver agreement" onClick={() => navigate("/drive-with-us")} />}
        <LinkRow icon={LogOut} label="Sign out" onClick={logout} danger testID="sign-out-row" />
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">Getaride Orlando · v1.0</p>
    </div>
  );
}
